import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  Delete,
  Search,
  Refresh,
  GroupAdd,
  CardMembership,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Club, ClubConfigurationStatus } from '../../types';
import RequireEmailVerification from '../../components/auth/RequireEmailVerification';
import { useTranslation } from 'react-i18next';
import { isAdmin } from '../../utils/auth';

const ClubsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchClub, setSearchClub] = useState('');
  const [clubConfigurations, setClubConfigurations] = useState<Record<string, ClubConfigurationStatus>>({});
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    id: string | null;
    name: string | null;
  }>({ open: false, id: null, name: null });

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get<Club[]>('/clubs');
      setClubs(response.data);
      
      // Fetch configuration status for each club
      const configPromises = response.data.map(club =>
        api.get<ClubConfigurationStatus>(`/clubs/${club.record_id}/configuration-status`)
          .then(res => ({ clubId: club.record_id, status: res.data }))
          .catch(() => ({ clubId: club.record_id, status: null }))
      );
      
      const configurations = await Promise.all(configPromises);
      const configMap: Record<string, ClubConfigurationStatus> = {};
      configurations.forEach(({ clubId, status }) => {
        if (status) {
          configMap[clubId] = status;
        }
      });
      setClubConfigurations(configMap);
    } catch (err: any) {
      setError('Failed to load clubs. Please try again.');
      console.error('Error fetching clubs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClub = async () => {
    if (!deleteDialog.id) return;
    
    try {
      await api.delete(`/club/${deleteDialog.id}`);
      setClubs(clubs.filter(c => c.record_id !== deleteDialog.id));
      setDeleteDialog({ open: false, id: null, name: null });
    } catch (err: any) {
      setError('Failed to delete club');
    }
  };

  const handleToggleOnSiteReg = async (clubId: string, currentStatus: boolean) => {
    try {
      await api.put(`/clubs/${clubId}/set-onsite-registration/${!currentStatus}`);
      // Update local state instead of refetching
      setClubs(prevClubs => 
        prevClubs.map(club => 
          club.record_id === clubId 
            ? { ...club, on_site_reg: !currentStatus }
            : club
        )
      );
    } catch (error: any) {
      console.error('Failed to toggle on-site registration:', error);
      // Show backend error message if available
      const errorMessage = error.response?.data?.detail || 'Failed to update on-site registration status';
      setError(errorMessage);
    }
  };

  const handleToggleVisitorClub = async (clubId: string, currentStatus: boolean) => {
    try {
      await api.put(`/clubs/${clubId}/set-visitor-club/${!currentStatus}`);
      // Update local state instead of refetching
      setClubs(prevClubs => 
        prevClubs.map(club => 
          club.record_id === clubId 
            ? { ...club, is_visitor_club: !currentStatus }
            : club
        )
      );
    } catch (error) {
      console.error('Failed to toggle visitor club status:', error);
      setError('Failed to update visitor club status');
    }
  };

  const filteredClubs = clubs.filter(club =>
    club.club_name.toLowerCase().includes(searchClub.toLowerCase()) ||
    (club.description || '').toLowerCase().includes(searchClub.toLowerCase())
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <RequireEmailVerification>
      <Container maxWidth="lg">
        <Box sx={{ 
          mb: 4, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {t('clubs.title')}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              variant="contained"
              startIcon={<GroupAdd />}
              onClick={() => navigate('/app/admin/create-club')}
              fullWidth={window.innerWidth < 600}
              sx={{
                background: 'linear-gradient(45deg, #ff00fb 30%, #ff4dff 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #cc00c8 30%, #ff00fb 90%)',
                },
              }}
            >
              {t('clubs.createClub')}
            </Button>
            {isAdmin() && (
              <Button
                variant="contained"
                startIcon={<CardMembership />}
                onClick={() => navigate('/app/admin/create-member')}
                fullWidth={window.innerWidth < 600}
                sx={{
                  background: 'linear-gradient(45deg, #ff4444 30%, #ff6666 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #cc2222 30%, #ff4444 90%)',
                  },
                }}
              >
                {t('clubs.createMember')}
              </Button>
            )}
            <IconButton onClick={fetchClubs} color="primary">
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Clubs Table */}
        <Paper elevation={3}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'stretch', sm: 'center' }, 
              gap: 2,
              mb: 2 
            }}>
              <Typography variant="h6">
                {t('clubs.clubsCount')} ({filteredClubs.length})
              </Typography>
              <TextField
                size="small"
                placeholder={t('clubs.search')}
                value={searchClub}
                onChange={(e) => setSearchClub(e.target.value)}
                sx={{ 
                  width: { xs: '100%', sm: 300 } 
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 300, sm: 600, md: 800 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('clubs.table.clubName')}</TableCell>
                  <TableCell>{t('clubs.table.description')}</TableCell>
                  <TableCell>{t('clubs.table.configuration')}</TableCell>
                  <TableCell>{t('clubs.table.type')}</TableCell>
                  <TableCell align="center">{t('clubs.table.onlineRegistration')}</TableCell>
                  <TableCell align="center">{t('clubs.table.visitorClub')}</TableCell>
                  <TableCell>{t('clubs.table.created')}</TableCell>
                  <TableCell align="center">{t('clubs.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClubs.map((club) => (
                  <TableRow key={club.record_id} hover>
                    <TableCell>{club.club_name}</TableCell>
                    <TableCell>{club.description}</TableCell>
                    <TableCell>
                      {clubConfigurations[club.record_id] ? (
                        clubConfigurations[club.record_id].is_configured ? (
                          <Chip
                            label={t('clubs.status.configured')}
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Tooltip title={`Missing: ${clubConfigurations[club.record_id].missing_fields.join(', ')}`}>
                            <Chip
                              label={`${clubConfigurations[club.record_id].missing_fields.length} ${t('clubs.status.fieldsNeeded', { defaultValue: 'fields needed' })}`}
                              size="small"
                              color={clubConfigurations[club.record_id].missing_fields.length > 3 ? "error" : "warning"}
                              onClick={() => navigate(`/app/admin/configure-club/${club.record_id}`)}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        )
                      ) : (
                        <Chip label="Loading..." size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {club.is_visitor_club ? (
                          <Chip
                            label={t('clubs.status.visitorClub')}
                            size="small"
                            sx={{ backgroundColor: '#ff00fb', color: 'white' }}
                          />
                        ) : (
                          <Chip label={t('clubs.status.standardClub')} size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip 
                        title={
                          !clubConfigurations[club.record_id]?.is_configured
                            ? `${t('clubs.status.cannotEnable')}: ${clubConfigurations[club.record_id]?.message || t('clubs.status.clubNeedsConfiguration')}`
                            : club.on_site_reg ? t('clubs.status.disableOnSiteRegistration') : t('clubs.status.enableOnSiteRegistration')
                        }
                      >
                        <span>
                          <Switch
                            checked={club.on_site_reg}
                            onChange={() => handleToggleOnSiteReg(club.record_id, club.on_site_reg)}
                            color="success"
                            disabled={!clubConfigurations[club.record_id]?.is_configured && !club.on_site_reg}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={club.is_visitor_club || false}
                        onChange={() => handleToggleVisitorClub(club.record_id, club.is_visitor_club || false)}
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      {club.created_at ? new Date(club.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title={t('clubs.manageClub')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/app/admin/configure-club/${club.record_id}`)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('clubs.deleteClub')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({
                              open: true,
                              id: club.record_id,
                              name: club.club_name,
                            })}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null, name: null })}>
          <DialogTitle>{t('clubs.deleteConfirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('clubs.deleteConfirmMessage')} "{deleteDialog.name}"?
              {t('clubs.deleteWarning')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, id: null, name: null })}>
              {t('clubs.cancel')}
            </Button>
            <Button
              onClick={handleDeleteClub}
              color="error"
              variant="contained"
            >
              {t('clubs.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </RequireEmailVerification>
  );
};

export default ClubsPage;